import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { User as UserIcon } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { getVisitsZoneSettings, listZoneVisits, type VisitSummaryDto } from '@/api/visits';
import { TotalScoreBadge } from '@/components/visits/scoreDisplay';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** Local-calendar-day bounds for the `day` (YYYY-MM-DD) prop — same
 *  "local day, loosely anchored" simplification OpsTasksPage's todayIso()
 *  already documents; the BE does not re-anchor visit_time to the zone's
 *  own timezone (unlike OpsTask.due_at), so this is approximate for zones
 *  far from the browser's timezone. Good enough for VISITS-3 v1. */
function dayBounds(day: string): { from: string; to: string } {
  const [y, m, d] = day.split('-').map(Number);
  const from = new Date(y, m - 1, d, 0, 0, 0);
  const to = new Date(y, m - 1, d, 23, 59, 59);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * The day's visits, merged into a report card — Alexey: "панелью на вкладке
 * Отчёт дня, разворачивается в полную". Each row expands into that visit's
 * own full page (VisitDetailPage, already the "full" view) rather than an
 * inline overlay, since a day can hold several visits. Hidden when visits
 * aren't enabled for the zone or there were none that day.
 */
export function DayVisitsCard({ zoneId, day }: { zoneId: string; day: string }) {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const navigate = useNavigate();

  const { data: settings } = useSWR(
    zoneId ? ['report:day-visits-settings', zoneId] : null,
    () => getVisitsZoneSettings(zoneId).then(r => r.data),
  );

  const { data } = useSWR(
    zoneId && settings?.enabled ? ['report:day-visits', zoneId, day] : null,
    () => {
      const { from, to } = dayBounds(day);
      return listZoneVisits(zoneId, { from, to }).then(r => r.data);
    },
  );

  if (!settings?.enabled) return null;
  const visits: VisitSummaryDto[] = (data?.results ?? []).filter(v => !v.is_draft);
  if (visits.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pt-4 pb-2">
        <CardTitle className={CARD_TITLE}>{t('visits.reportPanelCount', { n: visits.length })}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {visits.map(v => {
            const time = new Date(v.visit_time).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/usage/visits/${v.id}`)}
                  className="flex w-full items-center gap-2 py-1.5 text-left text-sm hover:text-primary"
                >
                  <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{v.author.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{time}</span>
                  <TotalScoreBadge total={v.total} />
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
